// ============================================================
// ANSICHTSSACHE-AGENT – Arbeitszeit eines Tages
// ============================================================
// ZWEI DINGE, DIE OFT VERWECHSELT WERDEN.
//
//   Arbeitszeit   wann jemand da war — Beginn, Ende, Pause.
//                 Aufzeichnungspflicht (§ 16 ArbZG), gehoert der
//                 Person und nicht dem Projekt.
//   Zeiteintrag   woran gearbeitet wurde — Projekt, Leistung,
//                 Dauer. Grundlage der Rechnung.
//
// Beides gehoert auf denselben Tag, aber nicht in dieselbe
// Tabelle: ein Tag mit 8h Anwesenheit und 6h30 gebuchter
// Leistung ist der Normalfall und kein Fehler. Erst die
// Gegenueberstellung macht die Luecke sichtbar — und genau die
// ist die Frage am Feierabend: „habe ich alles gebucht?"
//
// Die Uhrzeit liegt als MINUTEN SEIT MITTERNACHT vor und nicht
// als Zeitstempel. Ein Arbeitsbeginn ist keine Koordinate auf
// dem Zeitstrahl, sondern eine Angabe auf der Uhr: „08:51" heisst
// in Berlin wie in Lissabon 08:51, und keine Sommerzeit-
// umstellung soll daraus 07:51 machen. In der Datenbank steht
// deshalb der Text „HH:MM" (siehe `WorkTimeEntry`), hier wird
// damit gerechnet.
//
// Die Eingabe wird grosszuegig gelesen, das Ergebnis ist streng:
// eine Zahl oder `null`. Wer „9" tippt, meint 9 Uhr; wer „0851"
// tippt, kommt vom Ziffernblock. Dieselbe Haltung wie in
// `number-format.ts` — nur dort fuer Betraege.
// ============================================================

import { parseDecimal } from "@/lib/number-format";

/**
 * Die Zeitzone, in der das Haus arbeitet.
 *
 * Der Server laeuft auf UTC. Ohne diese Umrechnung waere ein
 * Feierabend um 23:30 Berliner Zeit im September ein Eintrag von
 * MORGEN — und der Arbeitstag, den man gerade beendet, waere auf
 * der Seite nicht mehr zu sehen.
 */
const HAUSZONE = "Europe/Berlin";

/** Der heutige Tag als `YYYY-MM-DD`, in der Zeitzone des Hauses. */
export function heuteImHaus(jetzt: Date = new Date()): string {
  // `sv-SE` ist das einzige Gebietsschema, das ISO-Datum liefert.
  return jetzt.toLocaleDateString("sv-SE", { timeZone: HAUSZONE });
}

/**
 * Der heutige Tag des Hauses als Wert fuer ein `@db.Date`-Feld
 * (UTC-Mitternacht). `new Date()` waere zwischen 0 und 2 Uhr
 * Berliner Zeit noch der Vortag — Prisma schneidet die Uhrzeit in
 * UTC ab.
 */
export function houseToday(jetzt: Date = new Date()): Date {
  return new Date(`${heuteImHaus(jetzt)}T00:00:00.000Z`);
}

/** Die laufende Minute des Tages (0–1439), in der Zeitzone des Hauses. */
export function minuteImHaus(jetzt: Date = new Date()): number {
  const [h, m] = jetzt
    .toLocaleTimeString("de-DE", { timeZone: HAUSZONE, hour12: false })
    .split(":")
    .map(Number);
  // Manche Laufzeiten geben Mitternacht als „24:00:00“ zurueck.
  return (h % 24) * 60 + m;
}

/** Ein Arbeitstag, wie ihn `WorkTimeEntry` haelt. */
export type Arbeitstag = {
  /** Minuten seit Mitternacht, `null` = noch nicht gekommen. */
  beginn: number | null;
  /** `null` = laeuft noch. */
  ende: number | null;
  /** Pausenminuten innerhalb der Spanne. */
  pause: number;
  homeoffice: boolean;
};

export const LEERER_TAG: Arbeitstag = {
  beginn: null,
  ende: null,
  pause: 0,
  homeoffice: false,
};

/** Ein Tag hat 24 Stunden — 24:00 ist als Arbeitsende noch erlaubt. */
const TAGESENDE = 24 * 60;

/**
 * Eine Uhrzeit aus dem, was jemand tippt.
 *
 * Erlaubt: `8:51`, `08:51`, `8.51`, `0851`, `851`, `8`. Die
 * vierstellige Form kommt vom Ziffernblock — dort ist der
 * Doppelpunkt der teuerste Anschlag. `null` heisst „keine
 * Uhrzeit", ausdruecklich nicht Mitternacht.
 */
export function zeitLesen(roh: string | null | undefined): number | null {
  if (roh === null || roh === undefined) return null;
  const text = String(roh).trim().replace(/\s/g, "");
  if (!text) return null;

  // Mit Trennzeichen: Stunde und Minute stehen da, wo sie stehen.
  const getrennt = text.match(/^(\d{1,2})[:.,uh](\d{1,2})$/i);
  if (getrennt) return pruefe(Number(getrennt[1]), Number(getrennt[2]));

  // Ohne Trennzeichen: die letzten zwei Ziffern sind die Minute,
  // eine oder zwei Ziffern allein sind die volle Stunde.
  const ziffern = text.match(/^(\d{1,4})$/);
  if (!ziffern) return null;
  const wert = ziffern[1];
  if (wert.length <= 2) return pruefe(Number(wert), 0);
  return pruefe(Number(wert.slice(0, -2)), Number(wert.slice(-2)));
}

function pruefe(stunde: number, minute: number): number | null {
  if (minute > 59) return null;
  const minuten = stunde * 60 + minute;
  if (minuten < 0 || minuten > TAGESENDE) return null;
  return minuten;
}

/** Minuten seit Mitternacht → `08:51`. Immer zweistellig. */
export function zeitText(minuten: number | null | undefined): string {
  if (minuten === null || minuten === undefined) return "";
  const m = Math.max(0, Math.min(TAGESENDE, Math.round(minuten)));
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

/** `08:51` aus der Datenbank → 531. Fehlerhafter Text ergibt `null`. */
export function zeitAusText(text: string | null | undefined): number | null {
  if (!text) return null;
  const treffer = /^(\d{1,2}):(\d{2})$/.exec(text.trim());
  return treffer ? pruefe(Number(treffer[1]), Number(treffer[2])) : null;
}

/**
 * Anwesende Minuten: Ende minus Beginn minus Pause.
 *
 * Solange kein Ende feststeht, zaehlt der Tag weiter — mit
 * `jetzt` als vorlaeufigem Ende. Das ist der Wert, der am
 * Nachmittag in der Leiste steht, und er muss mitlaufen.
 */
export function anwesend(tag: Arbeitstag, jetzt: number | null = null): number {
  if (tag.beginn === null) return 0;
  const ende = tag.ende ?? jetzt;
  if (ende === null) return 0;
  return Math.max(0, ende - tag.beginn - Math.max(0, tag.pause));
}

/** Ab dieser Arbeitszeit verlangt das Gesetz eine Pause (§ 4 ArbZG). */
export const PAUSE_PFLICHT_AB = 6 * 60;

/** Wo der Standardtag beginnt, solange die Person nichts eingestellt hat. */
export const STANDARD_BEGINN = "09:00";

/** Der Standardtag, fertig zum Eintragen — Uhrzeiten als `HH:MM`. */
export type Standardtag = {
  beginn: string;
  ende: string;
  pauseMinuten: number;
  homeoffice: boolean;
};

/**
 * Der Tag, wie er laut Arbeitszeitmodell aussieht, wenn nichts
 * dazwischenkommt: eigener Arbeitsbeginn, dazu das Soll des Tages
 * und die eigene Pause — daraus folgt das Ende.
 *
 * Die Pause zaehlt nur, wenn das Soll ueber sechs Stunden liegt.
 * Dieselbe Regel wie beim Ausstempeln: einem Vier-Stunden-Tag eine
 * halbe Stunde anzuhaengen, waere eine erfundene Anwesenheit.
 *
 * `null` an Tagen ohne Soll — und wenn das Ende ueber Mitternacht
 * fiele; einen solchen Standard gibt es nicht.
 */
export function standardtag({
  beginn,
  sollStunden,
  pauseMinuten,
  homeoffice,
}: {
  /** Was in den eigenen Einstellungen steht; leer = 09:00. */
  beginn: string | null | undefined;
  sollStunden: number;
  pauseMinuten: number | null | undefined;
  homeoffice: boolean;
}): Standardtag | null {
  const arbeit = Math.round(sollStunden * 60);
  if (!(arbeit > 0)) return null;
  const start = zeitLesen(beginn) ?? (zeitAusText(STANDARD_BEGINN) as number);
  const pause = arbeit > PAUSE_PFLICHT_AB ? Math.max(0, Math.round(pauseMinuten ?? 0)) : 0;
  const ende = start + arbeit + pause;
  if (ende > TAGESENDE) return null;
  return { beginn: zeitText(start), ende: zeitText(ende), pauseMinuten: pause, homeoffice };
}

/** Anwesende Minuten als Dezimalstunden, auf zwei Stellen — wie `totalHours`. */
export function alsStunden(minuten: number): number {
  return Math.round((minuten / 60) * 100) / 100;
}

/**
 * Was an einem Tag nicht stimmt — oder `null`.
 *
 * Bewusst nur die drei Faelle, die wirklich falsch sind. Ein
 * Arbeitstag von zehn Stunden ist keine Falscheingabe, sondern
 * ein langer Tag; die Bewertung nach Arbeitszeitgesetz gehoert
 * in die Personalakte und nicht in ein Eingabefeld.
 */
export function pruefeTag(tag: Arbeitstag): string | null {
  if (tag.beginn === null && tag.ende !== null) {
    return "Ohne Arbeitsbeginn kein Arbeitsende.";
  }
  if (tag.beginn !== null && tag.ende !== null && tag.ende <= tag.beginn) {
    return "Das Arbeitsende liegt vor dem Arbeitsbeginn.";
  }
  if (tag.beginn !== null && tag.ende !== null && tag.pause > tag.ende - tag.beginn) {
    return "Die Pause ist länger als der Arbeitstag.";
  }
  return null;
}

// ── Dauer einer Buchung ─────────────────────────────────────
// Die Dauer wird im Haus als DEZIMALSTUNDE gehalten (`hours` ist
// ein `Decimal`), getippt wird sie aber auf zwei Arten: „1,5"
// von denen, die Stunden rechnen, und „1:30" von denen, die auf
// die Uhr sehen. Beides ist richtig, also nimmt das Feld beides.

/** `1,5`, `1.5`, `1:30`, `90m`, `1h30` → 1.5 Stunden. `null` = keine Dauer. */
export function dauerLesen(roh: string | null | undefined): number | null {
  if (roh === null || roh === undefined) return null;
  const text = String(roh).trim().toLowerCase().replace(/\s/g, "");
  if (!text) return null;

  // Reine Minuten: „90m", „45min".
  const minuten = text.match(/^(\d+)m(?:in)?$/);
  if (minuten) return runde(Number(minuten[1]) / 60);

  // Uhrform: „1:30", „1h30", „1h" — die Minute ist nie groesser
  // als 59, sonst ist „1:90" gemeint gewesen und niemand weiss was.
  const uhr = text.match(/^(\d{1,2})[:h](\d{1,2})?$/);
  if (uhr) {
    const m = uhr[2] === undefined || uhr[2] === "" ? 0 : Number(uhr[2]);
    if (m > 59) return null;
    return runde(Number(uhr[1]) + m / 60);
  }

  const wert = parseDecimal(text);
  if (wert === null || wert < 0) return null;
  return runde(wert);
}

/** Zwei Nachkommastellen — mehr traegt eine Zeitbuchung nicht. */
function runde(stunden: number): number {
  return Math.round(stunden * 100) / 100;
}

/** Dezimalstunden → `1:30`. Die Schreibweise der Zeiterfassung, nicht die des Rechners. */
export function dauerText(stunden: number): string {
  const gesamt = Math.round(stunden * 60);
  const h = Math.floor(gesamt / 60);
  const m = gesamt % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}
